// Copyright (c) Bentley Systems
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectOption } from "@itwin/itwinui-react";
import { ITwin } from "../SubscribedProjects/Models/itwin";
import { ConfigManager } from "./config-manager";
import { mapDataToModels } from "./map-response";
import { setErrorState } from "./helper-functions";
import { AppDispatch } from "../app/store";
import { loadingSkeleton, searchEmptyMessage } from "./ComboBoxHelpers";

type UseITwinProjectsProps = {
  isEnabled: boolean;
  accessToken: string;
  dispatch: AppDispatch;
};

type UseITwinProjectsReturn = {
  projects: ITwin[];
  selectedProject: ITwin | null;
  setSelectedProject: (project: ITwin | null) => void;
  isProjectsLoading: boolean;
  nextLink: string | null;
  fetchNextProjects: () => void;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleProjectSelect: (project: ITwin | null) => void;
  selectOptions: SelectOption<ITwin>[];
  emptyContent: React.JSX.Element | string;
};

export const useITwinProjects = ({
  isEnabled,
  accessToken,
  dispatch,
}: UseITwinProjectsProps): UseITwinProjectsReturn => {
  // Constants
  const iTwinsCountForFetch = 50;

  // Keep accessToken in a ref so token refreshes don't recreate fetch callbacks
  // and trigger auto-fetch effects (which would clear the selected project).
  const accessTokenRef = useRef(accessToken);
  useEffect(() => { accessTokenRef.current = accessToken; }, [accessToken]);

  // State
  const [projects, setProjects] = useState<ITwin[]>([]);
  const [selectedProject, setSelectedProject] = useState<ITwin | null>(null);
  const [isProjectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [nextLink, setNextLink] = useState<string | null>(null);
  const [searchString, setSearchString] = useState<string>("");

  // Fetch projects function
  const fetchProjects = useCallback(async (search?: string, signal?: AbortSignal) => {
    if (!isEnabled) {
      return;
    }
    setNextLink(null);
    let url =
      `${ConfigManager.iTwinPlatformUrl}/itwins/?subClass=Project&$top=${iTwinsCountForFetch}&$select=displayName&$orderby=displayName`;
    if (search && search.trim().length > 0) {
      url += `&$search=${encodeURIComponent(search.trim())}`;
    }
    const runId = crypto.randomUUID();
    setProjectsLoading(true);
    await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessTokenRef.current,
        RunId: runId,
      },
      signal,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        } else {
          return response.json();
        }
      })
      .then(async function (data) {
        if (!data) {
          return;
        }
        const projectsDtos: ITwin[] = mapDataToModels<ITwin>(data.iTwins);
        setProjects(projectsDtos);
        setNextLink(data._links?.next?.href || null);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      })
      .finally(() => setProjectsLoading(false));
  }, [isEnabled, dispatch]);

  // Fetch next projects for pagination
  const fetchNextProjects = () => {
    if (!nextLink) {
      return;
    }
    setProjectsLoading(true);
    const runId = crypto.randomUUID();
    fetch(nextLink, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessTokenRef.current,
        RunId: runId,
      },
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }
        return response.json();
      })
      .then(function (data) {
        if (!data) {
          return;
        }
        const projectsDtos: ITwin[] = mapDataToModels<ITwin>(data.iTwins);
        setProjects((prev) => [...prev, ...projectsDtos]); // Append to existing
        setNextLink(data._links?.next?.href || null);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      })
      .finally(() => setProjectsLoading(false));
  };

  // When ComboBox selects an item it auto-fills the input with the label,
  // which fires inputProps.onChange.  We skip that event so the search state
  // stays unchanged and the dropdown keeps showing the full result list.
  const justSelectedRef = useRef(false);

  // Search handler — just update state, the debounced effect handles fetching
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    setSearchString(e.target.value);
  }, []);

  // Selection handler
  const handleProjectSelect = useCallback((project: ITwin | null) => {
    setSelectedProject(project);
    justSelectedRef.current = true;
  }, []);

  // Helper to map iTwin to SelectOption
  const mapITwinToSelectOption = (iTwin: ITwin): SelectOption<ITwin> => ({
    value: iTwin,
    label: iTwin.displayName,
  });

  const selectOptions = useMemo(
    () => projects.map(mapITwinToSelectOption),
    [projects],
  );

  // Empty content for ComboBox
  const emptyContent = useMemo(() => {
    if (isProjectsLoading) { return loadingSkeleton(6); }
    return searchEmptyMessage(searchString, 3);
  }, [isProjectsLoading, searchString]);

  // Combined effect for initial load + debounced search (like useIModels)
  useEffect(() => {
    if (!isEnabled) {
      setProjects([]);
      setSelectedProject(null);
      return;
    }

    const abortController = new AbortController();
    const trimmed = searchString.trim();

    // Empty → load unfiltered first page immediately.
    // 1-2 chars → clear results, show hint.
    // 3+ chars → debounced server search.
    if (trimmed.length > 0 && trimmed.length < 3) {
      setProjects([]);
      setNextLink(null);
      setProjectsLoading(false);
      return;
    }

    const searchArg = trimmed.length >= 3 ? trimmed : undefined;

    // Show skeleton immediately so "No options found" doesn't flash
    // during the debounce delay.
    if (searchArg) { setProjectsLoading(true); }

    const timer = setTimeout(() => {
      void fetchProjects(searchArg, abortController.signal);
    }, searchString === "" ? 0 : 400);

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [searchString, isEnabled, fetchProjects]);

  return {
    projects,
    selectedProject,
    setSelectedProject,
    isProjectsLoading,
    nextLink,
    fetchNextProjects,
    handleSearchChange,
    handleProjectSelect,
    selectOptions,
    emptyContent,
  };
};
